import { __assign, __awaiter, __extends, __generator } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright (c) 2019-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 * @author David Sehnal <david.sehnal@gmail.com>
 */
import { produce } from 'immer';
import { Canvas3DParams } from '../../mol-canvas3d/canvas3d';
import { PluginCommands } from '../../mol-plugin/commands';
import { StateTransform } from '../../mol-state';
import { Color } from '../../mol-util/color';
import { ParamDefinition as PD } from '../../mol-util/param-definition';
import { ParamMapping } from '../../mol-util/param-mapping';
import { PluginUIComponent } from '../base';
import { ParameterMappingControl } from '../controls/parameters';
import { ViewportHelpContent } from './help';
var SimpleSettingsControl = /** @class */ (function (_super) {
    __extends(SimpleSettingsControl, _super);
    function SimpleSettingsControl() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    SimpleSettingsControl.prototype.componentDidMount = function () {
        var _this = this;
        this.subscribe(this.plugin.events.canvas3d.settingsUpdated, function () { return _this.forceUpdate(); });
        this.subscribe(this.plugin.canvas3d.camera.stateChanged, function (state) {
            if (state.radiusMax !== undefined || state.radius !== undefined) {
                _this.forceUpdate();
            }
        });
    };
    SimpleSettingsControl.prototype.render = function () {
        if (!this.plugin.canvas3d)
            return null;
        return _jsxs(_Fragment, { children: [_jsx(ParameterMappingControl, { mapping: SimpleSettingsMapping }, void 0),
                _jsx(ViewportHelpContent, {}, void 0)] }, void 0);
    };
    return SimpleSettingsControl;
}(PluginUIComponent));
export { SimpleSettingsControl };
var LayoutOptions = {
    'sequence': 'Sequence',
    'log': 'Log',
    'left': 'Left Panel'
};
var SimpleSettingsParams = {
    spin: PD.Group({
        spin: Canvas3DParams.trackball.params.spin,
        speed: Canvas3DParams.trackball.params.spinSpeed
    }, { pivot: 'spin' }),
    camera: Canvas3DParams.camera,
    background: PD.Group({
        color: PD.Color(Color(0xFCFBF9), { label: 'Background', description: 'Custom background color' }),
        transparent: PD.Boolean(false)
    }, { pivot: 'color' }),
    lighting: PD.Group({
        renderStyle: Canvas3DParams.renderer.params.style,
        occlusion: Canvas3DParams.postprocessing.params.occlusion,
        outline: Canvas3DParams.postprocessing.params.outline,
        fog: Canvas3DParams.cameraFog,
    }, { pivot: 'renderStyle' }),
    clipping: PD.Group(__assign(__assign({}, Canvas3DParams.cameraClipping.params), Canvas3DParams.renderer.params.clip.params), { pivot: 'radius' }),
    layout: PD.MultiSelect([], PD.objectToOptions(LayoutOptions)),
};
var SimpleSettingsMapping = ParamMapping({
    params: function (ctx) {
        var _a;
        var params = PD.clone(SimpleSettingsParams);
        var controls = (_a = ctx.spec.components) === null || _a === void 0 ? void 0 : _a.controls;
        if (controls) {
            var options = [];
            if (controls.top !== 'none')
                options.push(['sequence', LayoutOptions.sequence]);
            if (controls.bottom !== 'none')
                options.push(['log', LayoutOptions.log]);
            if (controls.left !== 'none')
                options.push(['left', LayoutOptions.left]);
            params.layout.options = options;
        }
        return params;
    },
    target: function (ctx) {
        var _a, _b;
        var c = (_a = ctx.spec.components) === null || _a === void 0 ? void 0 : _a.controls;
        var r = ctx.layout.state.regionState;
        var layout = [];
        if (r.top !== 'hidden' && (!c || c.top !== 'none'))
            layout.push('sequence');
        if (r.bottom !== 'hidden' && (!c || c.bottom !== 'none'))
            layout.push('log');
        if (r.left !== 'hidden' && (!c || c.left !== 'none'))
            layout.push('left');
        return { canvas: (_b = ctx.canvas3d) === null || _b === void 0 ? void 0 : _b.props, layout: layout };
    }
})({
    values: function (props, ctx) {
        var canvas = props.canvas;
        var renderer = canvas.renderer;
        return {
            layout: props.layout,
            spin: { spin: !!canvas.trackball.spin, speed: canvas.trackball.spinSpeed },
            camera: canvas.camera,
            background: {
                color: renderer.backgroundColor,
                transparent: canvas.transparentBackground
            },
            lighting: {
                renderStyle: renderer.style,
                occlusion: canvas.postprocessing.occlusion,
                outline: canvas.postprocessing.outline,
                fog: canvas.cameraFog
            },
            clipping: __assign(__assign({}, canvas.cameraClipping), canvas.renderer.clip)
        };
    },
    update: function (s, props) {
        var canvas = props.canvas;
        canvas.trackball.spin = s.spin.spin;
        canvas.trackball.spinSpeed = s.spin.speed;
        canvas.camera = s.camera;
        canvas.transparentBackground = s.background.transparent;
        canvas.renderer.backgroundColor = s.background.color;
        canvas.renderer.style = s.lighting.renderStyle;
        canvas.postprocessing.occlusion = s.lighting.occlusion;
        canvas.postprocessing.outline = s.lighting.outline;
        canvas.cameraFog = s.lighting.fog;
        canvas.cameraClipping = {
            radius: s.clipping.radius,
            far: s.clipping.far,
        };
        canvas.renderer.clip = {
            variant: s.clipping.variant,
            objects: s.clipping.objects,
        };
        props.layout = s.layout;
    },
    apply: function (props, ctx) {
        return __awaiter(this, void 0, void 0, function () {
            var hideLeft, state;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, PluginCommands.Canvas3D.SetSettings(ctx, { settings: props.canvas })];
                    case 1:
                        _a.sent();
                        hideLeft = props.layout.indexOf('left') < 0;
                        state = produce(ctx.layout.state, function (s) {
                            s.regionState.top = props.layout.indexOf('sequence') >= 0 ? 'full' : 'hidden';
                            s.regionState.bottom = props.layout.indexOf('log') >= 0 ? 'full' : 'hidden';
                            s.regionState.left = hideLeft ? 'hidden' : ctx.behaviors.layout.leftPanelTabName.value === 'none' ? 'collapsed' : 'full';
                        });
                        return [4 /*yield*/, PluginCommands.Layout.Update(ctx, { state: state })];
                    case 2:
                        _a.sent();
                        if (hideLeft) {
                            PluginCommands.State.SetCurrentObject(ctx, { state: ctx.state.data, ref: StateTransform.RootRef });
                        }
                        return [2 /*return*/];
                }
            });
        });
    }
});
//# sourceMappingURL=simple-settings.js.map